import { isMobileUserAgent } from "../utils/device"
import {
  captureProgressReason,
  captureSummaryComplete,
  emptyCapturedState,
  mergeCaptureResult
} from "../wizard/screen_capture_state"

const TONE_CLASS = {
  success: "text-green-400",
  error: "text-red-400",
  info: "text-yellow-400",
  neutral: "text-gray-400"
}

export const screenCapturePresenter = {
  applyShareButtonGate() {
    if (!this.hasStartButtonTarget) return
    const opened = this.hasSubstepOpenTarget && this.substepOpenTarget.dataset.done === "true"
    this.startButtonTarget.disabled = !opened
    this.startButtonTarget.setAttribute("aria-disabled", opened ? "false" : "true")
  },

  markSubstepOpenDone() {
    if (this.hasSubstepOpenTarget) this.substepOpenTarget.dataset.done = "true"
    if (this.hasSubstepOpenBadgeTarget) {
      const badge = this.substepOpenBadgeTarget
      badge.classList.remove("bg-slate-900", "bg-slate-300")
      badge.classList.add("bg-emerald-500")
      badge.textContent = "✓"
    }
    if (this.hasOpenValidationLabelTarget) {
      this.openValidationLabelTarget.textContent = this.t("reopen_label", "Verification page opened — re-open")
    }
    if (this.hasSubstepOpenHintTarget) {
      this.substepOpenHintTarget.textContent = this.t("reopen_hint", "If you closed it by accident, tap to re-open.")
    }
    this.markSubstepShareActive(true)
    this.applyShareButtonGate()
  },

  markSubstepOpenPending() {
    if (this.hasSubstepOpenTarget) this.substepOpenTarget.dataset.done = "false"
    if (this.hasSubstepOpenBadgeTarget) {
      const badge = this.substepOpenBadgeTarget
      badge.classList.remove("bg-emerald-500", "bg-slate-300")
      badge.classList.add("bg-slate-900")
      badge.textContent = "1"
    }
    if (this.hasOpenValidationLabelTarget) {
      this.openValidationLabelTarget.textContent = this.t("open_label", "Open verification page")
    }
    if (this.hasSubstepOpenHintTarget) {
      this.substepOpenHintTarget.textContent = this.t("restart_hint", "Open a fresh verification tab, then start sharing again.")
    }
    this.markSubstepShareActive(false)
    this.applyShareButtonGate()
  },

  showInterruptionNotice() {
    if (this.hasInterruptionNoticeTarget) this.interruptionNoticeTarget.classList.remove("hidden")
  },

  hideInterruptionNotice() {
    if (this.hasInterruptionNoticeTarget) this.interruptionNoticeTarget.classList.add("hidden")
  },

  markSubstepShareActive(active) {
    if (this.hasSubstepShareTarget) {
      this.substepShareTarget.dataset.active = active && !this.completionReady() ? "true" : "false"
    }
    if (this.hasSubstepShareBadgeTarget) {
      const badge = this.substepShareBadgeTarget
      badge.classList.remove("bg-slate-300", "bg-slate-900", "bg-emerald-500")
      if (this.completionReady()) {
        badge.classList.add("bg-emerald-500")
        badge.textContent = "✓"
      } else if (active) {
        badge.classList.add("bg-slate-900")
        badge.textContent = "2"
      } else {
        badge.classList.add("bg-slate-300")
        badge.textContent = "2"
      }
    }
  },

  setStatus(message, tone) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove(...Object.values(TONE_CLASS))
    this.statusTarget.classList.add(TONE_CLASS[tone] || TONE_CLASS.neutral)
  },

  applyScreenShareEnvironmentHints() {
    if (!window.isSecureContext) {
      if (this.hasOpenValidationButtonTarget) this.openValidationButtonTarget.disabled = true
      if (this.hasStartButtonTarget) {
        this.startButtonTarget.disabled = true
        this.startButtonTarget.setAttribute("aria-disabled", "true")
      }
      this.setStatus(
        this.t("https_required", "Screen sharing requires HTTPS. Use a secure connection or choose Apple Wallet instead."),
        "error"
      )
      return
    }

    if (!this.screenShareApiAvailable()) {
      if (this.hasOpenValidationButtonTarget) this.openValidationButtonTarget.disabled = true
      if (this.hasStartButtonTarget) {
        this.startButtonTarget.disabled = true
        this.startButtonTarget.setAttribute("aria-disabled", "true")
      }
      this.setStatus(
        this.t("unsupported", "Screen sharing is not supported in this browser. Use a computer or choose Apple Wallet instead."),
        "error"
      )
      return
    }

    if (isMobileUserAgent()) {
      this.setStatus(
        this.t("mobile_warning", "Screen sharing on phones and tablets can be unreliable. Use a computer or choose Apple Wallet if needed."),
        "info"
      )
    }
  },

  clearWizardResult({ preserveCaptures = false } = {}) {
    if (!this.wizardModeValue) return

    const targets = [
      "hiddenComplete", "hiddenMatches", "hiddenConfidence", "hiddenReason",
      "hiddenVisibleName", "hiddenVisibleMembershipId", "hiddenVisibleTier", "hiddenCapturesJson"
    ]
    targets.forEach((targetName) => {
      const property = `has${targetName[0].toUpperCase()}${targetName.slice(1)}Target`
      const targetProperty = `${targetName}Target`
      if (this[property]) this[targetProperty].value = ""
    })

    if (!preserveCaptures) {
      this.verifiedWizardFiles = []
      this.capturedState = this.emptyCapturedState()
      this.renderCapturedState()
    }
  },

  persistWizardResult(data) {
    if (!this.wizardModeValue || !data) return false

    const beforeComplete = this.completionReady()
    const merged = this.mergeCaptureResult(data)
    this.renderCapturedState(data.reason)

    if (this.hasHiddenCompleteTarget) this.hiddenCompleteTarget.value = String(this.completionReady())
    if (this.hasHiddenMatchesTarget) this.hiddenMatchesTarget.value = String(this.completionReady())
    if (this.hasHiddenConfidenceTarget) this.hiddenConfidenceTarget.value = String(data.confidence ?? "")
    if (this.hasHiddenReasonTarget) this.hiddenReasonTarget.value = this.summaryReason(data.reason)
    if (this.hasHiddenVisibleNameTarget) this.hiddenVisibleNameTarget.value = this.capturedState.name.value || ""
    if (this.hasHiddenVisibleMembershipIdTarget) this.hiddenVisibleMembershipIdTarget.value = this.capturedState.membership.value || ""
    if (this.hasHiddenVisibleTierTarget) this.hiddenVisibleTierTarget.value = this.capturedState.tier.value || ""
    if (this.hasHiddenCapturesJsonTarget) this.hiddenCapturesJsonTarget.value = JSON.stringify(this.capturedState.captures)

    return merged || (!beforeComplete && this.completionReady())
  },

  emptyCapturedState() {
    return emptyCapturedState()
  },

  mergeCaptureResult(data) {
    return mergeCaptureResult(
      this.capturedState,
      data,
      this.collectApplicationFields(),
      this.evidenceLimitValue,
      this.minimumConfidenceValue
    )
  },

  summaryComplete() {
    return captureSummaryComplete(this.capturedState)
  },

  detailsCaptureComplete() {
    return this.summaryComplete() && this.minimumCaptureDurationReached()
  },

  completionReady() {
    return this.detailsCaptureComplete() &&
      this.finalConfirmationFramesCaptured >= this.finalConfirmationFramesValue
  },

  summaryMatches() {
    return this.completionReady()
  },

  summaryReason(fallbackReason = "") {
    if (this.summaryComplete()) {
      return this.t("summary_reason.complete", "Captured matching full name, airline program membership number, and current status.")
    }
    return fallbackReason || this.t("summary_reason.incomplete", "Navigate to pages that show your missing account details.")
  },

  captureProgressReason(lastReason = "") {
    return captureProgressReason(this.capturedState, lastReason, {
      complete: this.t("progress.complete", "All required details were captured."),
      name: this.t("progress.name", "full name"),
      membership: this.t("progress.membership", "airline program membership number"),
      tier: this.t("progress.tier", "current status"),
      stillNeed: this.t("progress.still_need", "Still need: %{fields}.")
    })
  },

  t(path, fallback) {
    const value = path.split(".").reduce((memo, key) => memo?.[key], this.i18nValue)
    return value == null || value === "" ? fallback : value
  },

  renderCapturedState() {
    this.updateCaptureCard()
    if (!this.hasCaptureCardTarget) return

    Object.entries(this.capturedState).forEach(([field, fieldState]) => {
      if (!["name", "membership", "tier"].includes(field)) return
      const row = this.captureCardTarget.querySelector(`[data-capture-field="${field}"]`)
      if (row) this.updateCaptureRow(row, row.querySelector("[data-capture-detected]"), fieldState)
    })
  },

  updateCaptureCard() {
    if (!this.hasCaptureCardTarget) return
    const state = this.completionReady() ? "complete" : this.running ? "capturing" : "idle"
    const current = this.captureCardTarget.dataset.state
    this.captureCardTarget.dataset.state = state

    this.captureCardTarget.querySelectorAll("[data-capture-card-state]").forEach((element) => {
      this.toggleVisibility(element, element.dataset.captureCardState === state)
    })

    if (state === "complete" && current !== "complete" && this.hasSuccessGifTarget) {
      const src = this.successGifTarget.getAttribute("src")
      if (src) this.successGifTarget.setAttribute("src", `${src.split("?")[0]}?t=${Date.now()}`)
    }
  },

  updateCaptureRow(rowElement, detectedValueElement, fieldState) {
    const matched = Boolean(fieldState?.matched)
    const seen = (fieldState?.lastSeen || "").trim()
    const state = matched ? "matched" : seen ? "detected" : this.running ? "capturing" : "idle"
    rowElement.dataset.rowState = state

    rowElement.querySelectorAll("[data-capture-row-state]").forEach((element) => {
      this.toggleVisibility(element, element.dataset.captureRowState === state)
    })

    if (detectedValueElement) {
      detectedValueElement.textContent = !matched && seen ? this.truncate(seen, 32) : ""
    }
  },

  truncate(value, max) {
    const string = String(value)
    return string.length > max ? `${string.slice(0, max - 1).trim()}…` : string
  },

  toggleVisibility(element, visible) {
    if (!element) return
    element.classList.toggle("hidden", !visible)
    element.toggleAttribute("hidden", !visible)
  }
}
